import { useEffect } from 'react';
import CommandPanel from './ui/CommandPanel.js';
import IconButton from './ui/IconButton.js';
import SectionHeader from './ui/SectionHeader.js';

// ProviderInfoModal v0.1.0 — Phase 2D Tier 0 click-popup.
// Full-screen overlay modal triggered by clicking a Tier 0 card body.
// Renders the multi-paragraph `services_covered` blurb plus a prominent
// "Open status page" link — the modal is the only path to navigate
// externally from a Tier 0 card. Closes on Esc, backdrop click, or the
// close button. `max-w-lg w-full max-h-[80vh] overflow-y-auto` keeps it
// scrollable on small screens.
interface Props {
  open: boolean;
  onClose: () => void;
  name: string;
  description: string;
  servicesCovered?: string;
  statusUrl: string;
}

export default function ProviderInfoModal({
  open,
  onClose,
  name,
  description,
  servicesCovered,
  statusUrl,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc handled via document-level keydown listener (see useEffect above); backdrop click is mouse-only by design
    // biome-ignore lint/a11y/useSemanticElements: native <dialog> requires imperative show/close API; we manage open state in React
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation only — no semantic action requires keyboard */}
      <div
        className="max-w-lg w-full max-h-[80vh] overflow-y-auto"
        // Clicks inside the dialog must not bubble up to the backdrop's
        // onClick (which would close it).
        onClick={(e) => e.stopPropagation()}
      >
        <CommandPanel bodyClassName="p-0 overflow-hidden">
          <SectionHeader
            title={<h2 className="font-mono text-sm tracking-[2px] text-accent">{name}</h2>}
            right={
              <IconButton as="button" label="Close" stopPropagation onClick={onClose}>
                ×
              </IconButton>
            }
          />
          <div className="px-4 py-3 space-y-3 text-sm leading-relaxed">
            <p className="text-text">{description}</p>
            {servicesCovered && (
              <p className="text-muted whitespace-pre-line text-xs">{servicesCovered}</p>
            )}
            <a
              href={statusUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-accent text-xs hover:underline"
            >
              Open status page ↗
            </a>
          </div>
        </CommandPanel>
      </div>
    </div>
  );
}
